/**
 * Progressive Trail Service
 *
 * Implements a staged loading approach for trail discovery:
 * 1. Load summary data first (fastest)
 * 2. Load weather-appropriate recommendations next
 * 3. Load complete trail data last
 *
 * Integrates with client-side cache and optimized data service
 */
import { optimizedTrailService, TrailSummary } from './OptimizedTrailService';
import { trailDiscoveryService } from './TrailDiscoveryService';
import { trailCache } from '@/utils/trailCache';
import {
  EnhancedTrailData,
  PointOfInterest,
  Season,
  WeatherCondition,
  CurrentWeather,
} from '@/types/enhancedTrail';
import { TrailWaypoint } from '@/types/hikingTrail';
import { BoundingBox } from '@/utils/pathSimplification';

/**
 * Stage completion callbacks
 */
export interface ProgressiveLoadingCallbacks {
  onSummaryLoaded: (data: TrailSummary) => void;
  onRecommendationsLoaded: (data: {
    weather: EnhancedTrailData[];
    season: EnhancedTrailData[];
  }) => void;
  onAllTrailsLoaded: (data: EnhancedTrailData[]) => void;
}

/**
 * Service that implements progressive loading of trail data
 */
export const progressiveTrailService = {
  // Get just the summary data first (fastest)
  async getSummaryData() {
    try {
      // Fetch minimal summary data
      const summary = await optimizedTrailService.getTrailSummary();
      return summary;
    } catch (err) {
      console.error('Error fetching summary data:', err);
      return null;
    }
  },

  // Get initial recommendations based on weather and season
  async getInitialRecommendations(weather, season) {
    try {
      // Safety check - if no weather data, return empty array
      if (!weather) {
        console.log('No weather data available for recommendations');
        return [];
      }

      // Get weather appropriate trails
      const weatherTrails =
        await optimizedTrailService.getWeatherRecommendedTrails(
          weather.condition
        );

      // Get seasonal trails
      const seasonalTrails =
        await optimizedTrailService.getSeasonalRecommendedTrails(season);

      // Ensure arrays are defined
      const safeWeatherTrails = weatherTrails || [];
      const safeSeasonalTrails = seasonalTrails || [];

      // Combine and remove duplicates
      const combinedTrails = [...safeWeatherTrails];
      const weatherTrailIds = new Set(safeWeatherTrails.map((t) => t.id));

      for (const trail of safeSeasonalTrails) {
        if (!weatherTrailIds.has(trail.id)) {
          combinedTrails.push(trail);
        }
      }

      return combinedTrails;
    } catch (err) {
      console.error('Error fetching initial recommendations:', err);
      return [];
    }
  },

  // Get all trails (lowest priority)
  async getAllTrails() {
    try {
      return await trailDiscoveryService.getEnhancedTrails();
    } catch (err) {
      console.error('Error fetching all trails:', err);
      return [];
    }
  },

  /**
   * Initialize progressive loading sequence
   * @param callbacks Callbacks for each loading stage
   * @param currentWeather Current weather for recommendations
   * @param currentSeason Current season for recommendations
   */
  async initializeLoading(callbacks, currentWeather, currentSeason) {
    // STAGE 1: Load summary data (fastest)
    await this.loadTrailSummary(callbacks.onSummaryLoaded);

    // STAGE 2: Load recommendations (based on conditions)
    await this.loadRecommendedTrails(
      currentWeather.condition,
      currentSeason,
      callbacks.onRecommendationsLoaded
    );

    // STAGE 3: Load all trails in background
    this.loadAllTrails(callbacks.onAllTrailsLoaded);
  },

  /**
   * Stage 1: Load summary data (fastest)
   */
  async loadTrailSummary(callback) {
    try {
      // Check client cache first
      let summary = await trailCache.getTrailSummary();

      if (!summary) {
        // Cache miss - fetch from network
        console.log('Trail summary cache miss, fetching from network');
        summary = await optimizedTrailService.getTrailSummary();

        // Cache the result if we got it
        if (summary) {
          await trailCache.cacheTrailSummary(summary);
        }
      } else {
        console.log('Trail summary loaded from cache');
      }

      if (summary) {
        // Execute callback with the data
        callback(summary);
      }
    } catch (error) {
      console.error('Error loading trail summary:', error);
      // Let the consumer handle the error case
    }
  },

  /**
   * Stage 2: Load recommended trails (based on conditions)
   */
  async loadRecommendedTrails(weatherCondition, season, callback) {
    try {
      // Load weather and seasonal recommendations in parallel
      const [weatherTrails, seasonalTrails] = await Promise.all([
        this.loadWeatherRecommendedTrails(weatherCondition),
        this.loadSeasonalRecommendedTrails(season),
      ]);

      callback({
        weather: weatherTrails || [],
        season: seasonalTrails || [],
      });
    } catch (error) {
      console.error('Error loading recommended trails:', error);
      // Provide empty results rather than failing completely
      callback({ weather: [], season: [] });
    }
  },

  /**
   * Load weather-appropriate recommended trails
   */
  async loadWeatherRecommendedTrails(weatherCondition) {
    try {
      // Always fetch from network for weather recommendations
      // They're small and dynamic based on current conditions
      const trails =
        await optimizedTrailService.getWeatherRecommendedTrails(
          weatherCondition
        );

      // Safety check
      const safeTrails = trails || [];

      // Cache individual trails for future reference
      for (const trail of safeTrails) {
        await trailCache.cacheTrail(trail.id, trail);
      }

      return safeTrails;
    } catch (error) {
      console.error('Error loading weather-recommended trails:', error);
      return [];
    }
  },

  /**
   * Load season-appropriate recommended trails
   */
  async loadSeasonalRecommendedTrails(season) {
    try {
      // Seasonal recommendations change infrequently, so fetch from network
      const trails =
        await optimizedTrailService.getSeasonalRecommendedTrails(season);

      // Safety check
      const safeTrails = trails || [];

      // Cache individual trails for future reference
      for (const trail of safeTrails) {
        await trailCache.cacheTrail(trail.id, trail);
      }

      return safeTrails;
    } catch (error) {
      console.error('Error loading seasonal-recommended trails:', error);
      return [];
    }
  },

  /**
   * Stage 3: Load all trails (lowest priority)
   */
  async loadAllTrails(callback) {
    try {
      // Get summary first to check if it's worth loading all trails
      const summary = await optimizedTrailService.getTrailSummary();

      if (!summary || summary.trailCount === 0) {
        console.log('No trails to load or summary unavailable');
        callback([]);
        return;
      }

      // If there are many trails, don't try to load them all at once
      const MAX_TRAILS_TO_LOAD = 50;
      if (summary.trailCount > MAX_TRAILS_TO_LOAD) {
        console.log(
          `Too many trails (${summary.trailCount}) to load all at once`
        );
        // Just load the top rated trails instead
        const topTrailIds = summary.topRatedTrails || [];
        const topTrails = [];

        for (const trailId of topTrailIds) {
          // Check cache first
          let trail = await trailCache.getTrail(trailId);

          if (!trail) {
            // Cache miss - fetch from network
            trail = await optimizedTrailService.getTrailById(trailId);

            if (trail) {
              await trailCache.cacheTrail(trailId, trail);
            }
          }

          if (trail) {
            topTrails.push(trail);
          }
        }

        callback(topTrails);
        return;
      }

      // Otherwise, load all trails (still a realistic number)
      const allTrails = await trailDiscoveryService.getEnhancedTrails();

      // Safety check
      const safeTrails = allTrails || [];

      // Cache all trails
      for (const trail of safeTrails) {
        await trailCache.cacheTrail(trail.id, trail);
      }

      callback(safeTrails);
    } catch (error) {
      console.error('Error loading all trails:', error);
      callback([]);
    }
  },

  /**
   * Get trail with progressive detail loading
   * @param trailId The trail ID to load
   * @param callbacks Optional callbacks for loading stages
   */
  async getTrailWithProgressiveLoading(trailId, callbacks) {
    try {
      // STAGE 1: Try to get from cache first (fastest)
      let trail = await trailCache.getTrail(trailId);

      if (trail && callbacks?.onBasicInfoLoaded) {
        callbacks.onBasicInfoLoaded(trail);
      }

      // STAGE 2: Get full details from network
      const fullTrail = await optimizedTrailService.getTrailById(trailId);

      if (!fullTrail) {
        console.error(`Trail ${trailId} not found`);
        return trail; // Return cached version if available, or null
      }

      // Cache the full trail details
      await trailCache.cacheTrail(trailId, fullTrail);

      if (callbacks?.onFullDetailsLoaded) {
        callbacks.onFullDetailsLoaded(fullTrail);
      }

      // STAGE 3: Load all waypoints (lowest priority)
      // For the optimization plan, we'll make this conditional
      // based on the user's needs and viewport

      // Return the most complete version we have
      return fullTrail;
    } catch (error) {
      console.error(`Error loading trail ${trailId}:`, error);
      return null;
    }
  },

  /**
   * Get waypoints for a trail with appropriate detail level
   * @param trailId The trail ID
   * @param zoomLevel Current map zoom level
   * @param viewport Optional viewport bounding box
   */
  async getWaypointsForTrail(trailId, zoomLevel, viewport) {
    try {
      // Determine detail level based on zoom
      let detailLevel = 'high';

      if (zoomLevel < 10) {
        detailLevel = 'summary';
      } else if (zoomLevel < 13) {
        detailLevel = 'low';
      } else if (zoomLevel < 15) {
        detailLevel = 'medium';
      }

      // Try to get from cache first
      let waypoints = await trailCache.getWaypoints(trailId, detailLevel);

      if (waypoints.length > 0) {
        console.log(
          `Found ${waypoints.length} waypoints in cache for ${trailId} at ${detailLevel} detail`
        );
        return waypoints;
      }

      // Cache miss - fetch from network
      console.log(
        `Waypoints cache miss for ${trailId} at ${detailLevel} detail`
      );
      waypoints = await optimizedTrailService.getWaypointsForTrail(
        trailId,
        zoomLevel,
        viewport
      );

      // Safety check
      const safeWaypoints = waypoints || [];

      // Cache the waypoints
      if (safeWaypoints.length > 0) {
        await trailCache.cacheWaypoints(trailId, detailLevel, safeWaypoints);
      }

      return safeWaypoints;
    } catch (error) {
      console.error(`Error fetching waypoints for trail ${trailId}:`, error);
      return [];
    }
  },

  /**
   * Get points of interest within the current viewport
   * @param viewport The current viewport bounding box
   */
  async getPOIsInViewport(viewport) {
    try {
      // Try to get from cache first
      let pois = await trailCache.getPOIsInViewport(viewport);

      if (pois.length > 0) {
        return pois;
      }

      // Cache miss - fetch from network
      pois = await optimizedTrailService.getPOIsInViewport(viewport);

      // Safety check
      const safePois = pois || [];

      // Cache the POIs
      if (safePois.length > 0) {
        await trailCache.cachePOIsForViewport(viewport, safePois);
      }

      return safePois;
    } catch (error) {
      console.error('Error fetching POIs in viewport:', error);
      return [];
    }
  },
};
